import { apiFetch, apiUpdate, getApiBase, type RequestOptions } from './client'
import {
  type Document,
  type Resource,
  USERS_TYPE,
  type UserAttributes,
  type UserScope,
} from './types'

/**
 * A user as the components see one: the API's snake_case record reduced to the
 * fields that are shown or edited anywhere in the bundle. The session caches
 * this shape, the account portal edits it, so it lives here rather than with
 * either of them.
 */
export interface User {
  id: string
  name: string
  email: string | null
  /** The generated referral code. What a child types into Studio; never editable. */
  username: string
  scope: UserScope
  privateProfile: boolean
}

/** The platform's own rule for a first name: everything before the first space. */
export function firstName(user: Pick<User, 'name'>): string {
  return user.name.trim().split(/\s+/)[0] || user.name
}

/** The rest of the name, by the same rule; empty for a name with no space. */
export function lastName(user: Pick<User, 'name'>): string {
  return user.name.trim().split(/\s+/).slice(1).join(' ')
}

/** A name as a possessive: Desmond’s, but James’ — a name ending in s takes the apostrophe alone. */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`
}

export function toUser(resource: Resource): User {
  const attributes = resource.attributes as UserAttributes | undefined
  if (!attributes) throw new Error(`User ${resource.id} came back without attributes`)

  return {
    id: resource.id,
    name: attributes.name,
    email: attributes.email ?? null,
    username: attributes.username,
    scope: attributes.scope,
    privateProfile: attributes.private_profile,
  }
}

/**
 * A user's avatar. The API answers with a redirect to the image, or to a
 * default when none is set, and needs no token — so it is a plain `<img>` source.
 */
export function avatarUrl(id: string): string {
  return `${getApiBase()}/img/users/${id}/thumbnail`
}

export async function fetchUser(id: string, options: RequestOptions = {}): Promise<User> {
  const document = await apiFetch<Document<Resource>>(`/users/${id}`, options)
  return toUser(document.data)
}

/** What the portal may change. Everything else on the record is read-only there. */
export interface UserUpdate {
  name?: string
  privateProfile?: boolean
  password?: string
  /**
   * Sent when a user changes their own password. The platform is being changed
   * to verify it; until then it is accepted and ignored, so sending it from the
   * start means enforcement arrives with no change on this side.
   */
  currentPassword?: string
}

const ATTRIBUTE_NAMES = {
  name: 'name',
  privateProfile: 'private_profile',
  password: 'password',
  currentPassword: 'current_password',
} as const satisfies Record<keyof UserUpdate, string>

/** Only the fields actually being changed, in the API's naming. */
function toUserAttributes(update: UserUpdate): Record<string, unknown> {
  const attributes: Record<string, unknown> = {}
  for (const [field, value] of Object.entries(update)) {
    if (value !== undefined) attributes[ATTRIBUTE_NAMES[field as keyof UserUpdate]] = value
  }
  return attributes
}

export async function updateUser(
  id: string,
  update: UserUpdate,
  options: RequestOptions = {},
): Promise<User> {
  const document = await apiUpdate<Document<Resource>>(
    `/users/${id}`,
    { type: USERS_TYPE, id, attributes: toUserAttributes(update) },
    options,
  )
  return toUser(document.data)
}
